import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FilterIcon from '@mui/icons-material/FilterListOutlined';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import DocumentsTable from './DocumentsTable';
import DocumentsTableMobile from './DocumentsTableMobile';
import DocumentModal from './DocumentModal';
import FilterDocumentsModal from './FilterDocumentsModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import {
  fetchDocuments,
  deleteDocument,
  addDocument,
  updateDocument,
} from '../../actions/adminActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './documents.scss';
import MuiIconButton from '@mui/material/IconButton';

const DocumentsTableContainer = (props) => {
  const {
    documents = [],
    distributors,
    addDocument,
    deleteDocument,
    fetchDocuments,
    withButton,
    toggleSidebar,
    countries,
    updateDocument,
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const [showFilterModal, toggleFilterModal] = useState(false);
  const [filterState, setFilterState] = useState({ countries: [] });

  const onEditClick = (document) => {
    setSelectedDocument(document);
    toggleUpdateModal(true);
  };

  const refreshDocuments = async () => {
    try {
      setFetching(true);
      await fetchDocuments();
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const onDocumentCreate = async (document, description, countries) => {
    try {
      setLoading(true);
      await addDocument({ ...document, description: description || '', countries: countries || [] });
      await refreshDocuments();
      toggleCreateModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onDocumentUpdate = async (document, description, countries) => {
    if (selectedDocument) {
      try {
        setLoading(true);
        await updateDocument(selectedDocument?.id, { description: description || '', countries: countries || [] });
        await refreshDocuments();
        toggleUpdateModal(false);
        setSelectedDocument(null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  const onDeleteDocument = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteDocument(selectedId);
        await fetchDocuments();
        toggleDeleteModal(false);
        setSelectedId(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const onSubmitFilters = (filters) => {
    setFilterState({
      distributors: filters.distributors || [],
      roles: filters.roles || [],
      countries: filters.countries || [],
    });
    toggleFilterModal(false);
  };

  const onRemoveFilterItem = (key, value) => {
    setFilterState({
      ...filterState,
      [key]: filterState[key].filter((v) => v !== value),
    })
  }

  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    if (search) {
      const q = search.toLowerCase();
      filtered = documents.filter((p) => p?.name?.toLowerCase()?.includes(q));
    }

    if (filterState?.countries?.length) {
      filtered = filtered?.filter((d) => d?.countries?.some((country) => filterState?.countries?.includes(country)));
    }

    return filtered;
  }, [documents, search, filterState]);

  const list = useMemo(() => {
    let result = filteredDocuments;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredDocuments, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <MuiIconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </MuiIconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Documents
                  <Box className="badge">{documents.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    View and manage documents
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <IconButton
              onClick={() => toggleFilterModal(true)}
              icon={<FilterIcon />}
              sx={{ mr: 2 }}
            />
            <Button
              text='Add Document'
              onClick={() => toggleCreateModal(true)}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {documents.length ? (
          <Fragment>
            {filterState &&
              (Object.keys(filterState)?.some((key) => filterState[key]?.length)
                && (
                  <div className="filter-list">
                    <FiltersIcon color="secondary" />
                    {Object.keys(filterState).map((key) => (
                      <div key={key} className="filter-list-item-container">
                        {filterState[key].map((value) => (
                          <div key={`${key}-${value}`} className="filter-list-item">
                            <div />
                            <div className="filter-list-item-value">{value}</div>
                            <CloseIcon color="secondary" onClick={() => onRemoveFilterItem(key, value)} />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))
            }
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <DocumentsTable
                documents={list}
                distributors={distributors}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredDocuments?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <DocumentsTableMobile documents={list} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Documents</Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteDocument}
        title="Delete Document"
        text="Are you sure you want to delete this document?"
        cancelText="Cancel"
        submitText="Delete"
        loading={loading}
        variant="error"
      />

      <DocumentModal
        open={showCreateModal}
        onClose={() => toggleCreateModal(false)}
        onSubmit={onDocumentCreate}
        deleteDocument={deleteDocument}
        countriesList={countries}
        loading={loading}
      />

      {selectedDocument && (
        <DocumentModal
          open={showUpdateModal}
          onClose={() => toggleUpdateModal(false)}
          onSubmit={onDocumentUpdate}
          // deleteDocument={deleteDocument}
          countriesList={countries}
          loading={loading}
          mode='update'
          initialValues={selectedDocument}
        />
      )}

      <FilterDocumentsModal
        filter={filterState}
        open={showFilterModal}
        onClose={() => toggleFilterModal(false)}
        onSubmit={onSubmitFilters}
        countries={countries}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteDocument}
        title="Delete Document?"
        text="Are you sure you want to delete this document? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </>
  );
};

DocumentsTableContainer.propTypes = {
  documents: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchDocuments: () => dispatch(fetchDocuments()),
    addDocument: (documentData) => dispatch(addDocument(documentData)),
    updateDocument: (docId, documentData) => dispatch(updateDocument(docId, documentData)),
    deleteDocument: (id) => dispatch(deleteDocument(id)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DocumentsTableContainer);
